import {Component, OnInit, OnDestroy, EventEmitter, ElementRef, Output, ViewChild} from "@angular/core";
import { Router } from "@angular/router";
import {
    implementsTKeys,
    ITCIApplicationCreateProvider,
    ITCIApplicationCreateRequest,
    ITCIApplicationCreateResponse,
    ITCIFileMap,
    ITCIMarketplaceListing,
    ITCIValidationError,
    ITCIValidationResult,
    mapIServiceToTenantAppType,
    TCI_CREATE_APP_UI_TYPE,
    TCIActionState,
    TCIActionValidationResult,
    TCIApplicationCreateOption,
    TCIMainMenu,
    TCIMainMenuSubroutes,
    TCITenantAppType,
    TCITenantAppTypeMetaData,
    TCIApplicationCreateCategoryGroup,
    TCIApplicationCreateOptionSubButton,
    ITCIApplicationCreateOption,
    TCITenantValidationResult,
    TCIValidationResultType,
    TCIApplicationCreateCategory,
    ValidationError
} from "@tci/common";
import {NgMarketplaceV2Service} from "../../ng-marketplace-v2/ng-marketplace-v2.service";
import { NgUserService } from "../../ng-shared/ng-services/ng-user.service";
import { NgToolsService } from "../../ng-shared/ng-util/ng-tools.service";

import {TciTenantService} from "../../ng-tenant/tci-tenant.service";
import {TciTenantModelService} from "../../ng-tenant/tci-tenant.model";
import {
    ITCITenantCacheOptions,
    TciTenantComponentCacheService
} from "../../ng-tenant/tci-tenant-component-cache.service";
import {NgToastrService} from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import {NgAppListService} from "../ng-app-list.service";
import {TCITenantRemoteServices} from "../../ng-tenant/tci-tenant-remote-services";
import {AbstractTciTenantComponentLoader} from "../../ng-tenant/tci-tenant-component-loader.component";
import {ITCIUploadFileCheckResult} from "../../ng-tenant/tci-tenant-components";
import {
    TCITenantCreateAppChooserV2ComponentInput,
    TCITenantCreateAppImportV2ComponentInput,
    TCITenantCreateAppV2ComponentInput,
    TCITenantCreateAppGraphQLImportV2ComponentInput,
    TCITenantCreateAppMarketplaceV2ComponentInput
} from "./base-components/extended-components";

import * as lodash from "lodash";
import * as CryptoJS from "crypto-js";
import {Subscription} from "rxjs";

export interface ISolutionOption {
    type: TCITenantAppType;
    title?: string;
    description?: string;
}

export interface IAppCreationResponse {
    solution: ISolutionOption;
    response: ITCIApplicationCreateResponse;
}

const MANIFEST_JSON = "manifest.json";
const GAL_LOCAL_ITEM = "TCI-GAL-LOCAL-DATA";
const GAL_LOCAL_CREATE_PREFIX = "tci_gal_create_";

@Component({
    selector: "ng-app-create",
    templateUrl: "./ng-app-create-v2.component.html",
    styleUrls: ["./ng-app-create-v2.component.less"]
})
export class NgAppCreateV2Component implements OnInit, OnDestroy {
    @Output() RequestStart = new EventEmitter();
    @Output() RequestFailed: EventEmitter<IAppCreationResponse> = new EventEmitter();
    @Output() RequestSucceed: EventEmitter<IAppCreationResponse> = new EventEmitter();
    @Output() RequestEnd = new EventEmitter();
    @Output() RequestEndWithWarnings = new EventEmitter();
    @Output() Close = new EventEmitter();
    @Output() RefreshAppList = new EventEmitter();
    @ViewChild("importerCtlr") ImporterController: AbstractTciTenantComponentLoader<any, any, any>;
    @ViewChild("createAppDetailEle") createAppDetailEle: ElementRef;
    @ViewChild("optionButtonScrollEle") optionButtonScrollEle: ElementRef;

    createCategory: TCIApplicationCreateCategoryGroup[] = [];
    scratchMetaData: TCITenantAppTypeMetaData[] = [];
    selectedScratchMetaData: TCITenantAppTypeMetaData;
    selectedMetaData: TCITenantAppTypeMetaData;
    selectedOption: TCIApplicationCreateOption;
    usedCreateOption: TCIApplicationCreateOption[] = [];

    selectedOptionBtns: TCIApplicationCreateOptionSubButton[] = [];
    selectedTenantProv: ITCIApplicationCreateProvider;
    selectedTenantCanCreate = true;
    selectedBtn: TCIApplicationCreateOptionSubButton;
    selectedMKListing: ITCIMarketplaceListing = null;
    isSelectedQuickstart = true;
    isSelectedScratch = false;
    isShowLeftScrollHintIcon = false;
    isShowRightScrollHintIcon = false;

    isLoading = true;
    isScrollLeftDisabled = true;
    isScrollRightDisabled = false;
    toggleOptionGroupMap = {};

    input: ITCIApplicationCreateRequest;
    allowToPublish = false;
    maxOptionButtonInRow = 5;
    maxRecentUsedApp = 5;
    quickStartDescription = TCITenantAppTypeMetaData.QUICKSTART.description;
    allAppTypesDescription = TCITenantAppTypeMetaData.ALL_APP_TYPES.description;

    localStorageKey = "";
    showImportCmp = false;
    tenantInput: TCITenantCreateAppV2ComponentInput;
    tenantMarketplaceInput: TCITenantCreateAppMarketplaceV2ComponentInput;
    tenantImportInput: TCITenantCreateAppImportV2ComponentInput;
    tenantAPIChooserInput: TCITenantCreateAppChooserV2ComponentInput;
    tenantGraphQLSchemaInput: TCITenantCreateAppGraphQLImportV2ComponentInput;

    // After create
    warns: ITCIValidationError[] = [];
    response: IAppCreationResponse; // To store the response if warnings
    private subscriptions: Subscription[] = [];

    constructor(
        private tenantSvc: TciTenantService,
        private tenantModelSvc: TciTenantModelService,
        private cacheSvc: TciTenantComponentCacheService,
        private tenantRemoteServices: TCITenantRemoteServices,
        public AppsAPISvc: NgAppListService,
        private ToastrSvc: NgToastrService,
        private toolsSvc: NgToolsService,
        private userService: NgUserService,
        private mkService: NgMarketplaceV2Service,
        private router: Router
    ) {}

    ngOnInit() {
        this.subscriptions.push(
            this.tenantSvc.onInit().subscribe(() => this.setAppCreateOptions()),
            this.mkService.getCreatingAppFromMiniListSubject()
                .subscribe(isCreating => {
                    if (isCreating) {
                        this.RequestStart.emit();
                    } else {
                        this.rememberUsedCreateOption(this.selectedOption);
                        this.RequestEnd.emit();
                    }
                })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions
            .filter(s => !s.closed)
            .forEach(s => s.unsubscribe());
    }

    setAppCreateOptions() {
        this.tenantRemoteServices.getRemoteServices()
            .map(svcs => svcs.map(svc => mapIServiceToTenantAppType(svc).toString()))
            .subscribe(svcs => {
                this.isLoading = false;
                this.subscriptions.push(
                    this.userService.getUserInfo()
                    .subscribe((userInfo) => {
                        this.localStorageKey = this.getGALPrefForUsr(userInfo.userId);
                        this.usedCreateOption = this.getLocalStorageDataForUsedCreateOption()
                            // Note: only show maxRecentUsedApp items with tenant available in used create option
                            .filter(item => {
                                return svcs.includes(item.type.toString());
                            })
                            .slice(0, this.maxRecentUsedApp);
                    })
                );

                this.createCategory = lodash.cloneDeep(TCITenantAppTypeMetaData.getAllItems());
                this.createCategory.forEach(cat => {
                    cat.options.forEach(item => {
                        item.buttons = item.buttons.filter(button => {
                            return svcs.includes(button.type.toString());
                        });
                        // Note: skip option Quickstart and all app types, because there is no default option for it.
                        if (!this.isSelectQuickstart(item) && !this.isSelectAllAppTypes(item)) {
                            // Note: default option priority level: defaultOption > recommendOption > buttons[0]
                            if (!item.defaultOption || (item.defaultOption && !svcs.includes(item.defaultOption.type.toString()))) {
                                if (!item.recommendOption || (item.recommendOption && !svcs.includes(item.recommendOption.type.toString()))) {
                                    item.defaultOption = item.buttons.length ? item.buttons[0] : null;
                                } else {
                                    item.defaultOption = lodash.cloneDeep(item.recommendOption);
                                }
                            }
                        }
                    });
                });
                this.selectedMetaData = TCITenantAppTypeMetaData.QUICKSTART;

                this.scratchMetaData = lodash.cloneDeep(TCITenantAppTypeMetaData.getScratchItems());
                this.scratchMetaData.forEach(data => {
                    data.buttons = data.buttons.filter(button => {
                        return svcs.includes(button.type.toString());
                    });
                    // Note: add null in the button list every maxOptionButtonInRow items, that is for tenant-dync-load element
                    //       array buttons looks like [a,b,c,e,f,null,1,2,3,4,5,null]
                    let pos = this.maxOptionButtonInRow;
                    while (pos < data.buttons.length) {
                        data.buttons.splice(pos, 0, null);
                        pos += this.maxOptionButtonInRow + 1;
                    }
                    if (data.buttons.length > 0 && lodash.last(data.buttons) !== null) {
                        data.buttons.push(null);
                    }
                });
            });
    }

    // Note: when left side category does not have child option menu, hide it.
    isShowCreateCategoryMenu(options: TCITenantAppTypeMetaData[], category: TCIApplicationCreateCategory) {
        let sum = 0;
        options.forEach(item => sum += item.buttons.length);
        return sum > 0 || TCIApplicationCreateCategory.QUICKSTART.id === category.id;
    }

    // Note: when left side option menu does not have create option buttons. hide it
    isShowCreateOptionMenu(option: TCITenantAppTypeMetaData) {
        return option.buttons.length > 0 || this.isSelectQuickstart(option) || this.isSelectAllAppTypes(option);
    }

    private getGALPrefForUsr(userId: string): string {
        return GAL_LOCAL_CREATE_PREFIX + CryptoJS.SHA256(GAL_LOCAL_ITEM + userId).toString();
    }

    private rememberUsedCreateOption(option: TCIApplicationCreateOption) {
        if (lodash.some(this.usedCreateOption, option)) {
            lodash.remove(this.usedCreateOption, option);
        }
        // Note: recent used option will be the first item.
        this.usedCreateOption.unshift(option);

        this.setLocalStorageDataForUsedCreateOption();
    }

    private getLocalStorageDataForUsedCreateOption(): TCIApplicationCreateOption[] {
        const localData = localStorage.getItem(this.localStorageKey);
        let arr = [];
        if (localData) {
            try {
                const arrOption = JSON.parse(localData);
                // Note: get used create option button base on the stored arr
                arr = TCIApplicationCreateOption.allOptions.filter(item => arrOption.includes(item.option));
                // Note: sort arr(create option list) base on the stored arrOption index
                arr = lodash.sortBy(arr, item => {
                    return arrOption.indexOf(item.option);
                });
            } catch (e) {
                console.error("GAL stage restore incomplete", e);
            }
        }
        return arr;
    }

    private setLocalStorageDataForUsedCreateOption() {
        const arrOption = [];
        this.usedCreateOption.forEach(item => arrOption.push(item.option));
        localStorage.setItem(this.localStorageKey, JSON.stringify(arrOption));
    }

    private isSelectQuickstart(option: TCITenantAppTypeMetaData) {
        return TCITenantAppTypeMetaData.QUICKSTART.equals(option);
    }

    private isSelectAllAppTypes(option: TCITenantAppTypeMetaData) {
        return TCITenantAppTypeMetaData.ALL_APP_TYPES.equals(option);
    }

    selectMetaData(metaData: TCITenantAppTypeMetaData) {
        if (this.selectedMetaData && this.selectedMetaData.equals(metaData)) {
            // select same option, ignore this action
            return ;
        }
        this.resetCreateDialog();

        this.selectedMetaData = metaData;
        if (this.isSelectQuickstart(metaData)) {
            this.isSelectedQuickstart = true;
        } else if (this.isSelectAllAppTypes(metaData)) {
            this.isSelectedScratch = true;
        } else {
            this.selectOptionButton(metaData.defaultOption);
            this.resetScrollButton();
        }
    }

    selectOptionButton(option: TCIApplicationCreateOption,
                       scratchMetaData?: TCITenantAppTypeMetaData) {
        if (this.selectedOption && this.selectedOption.equals(option)) {
            // select same option ignore this action
            return ;
        }
        if (scratchMetaData) {
            this.selectedScratchMetaData = scratchMetaData;
        } else {
            this.mkService.refreshMiniListSubject(true);
        }
        this.resetSelectOptionButton();
        if (TCIApplicationCreateOption.IMPLEMENT_AN_OPENAPI_SPEC.equals(option)) {
            this.tenantAPIChooserInput = new TCITenantCreateAppChooserV2ComponentInput(option.type, option);
        }
        if (TCIApplicationCreateOption.IMPORT_A_GRAPHQL_SCHEMA.equals(option)) {
            this.tenantGraphQLSchemaInput = new TCITenantCreateAppGraphQLImportV2ComponentInput(option.type, option);
        }
        // Note: can not use [].includes(), because of the item of array is an object
        if (lodash.some([
            TCIApplicationCreateOption.IMPORT_A_NODEJS_APP,
            TCIApplicationCreateOption.IMPORT_A_FLOGO_APP,
            TCIApplicationCreateOption.IMPORT_A_SCHEDULED_APP,
            TCIApplicationCreateOption.IMPORT_A_EVENT_APP,
            TCIApplicationCreateOption.IMPORT_A_BUSINESSWORKS_APP
        ], option)) {
            this.tenantImportInput = new TCITenantCreateAppImportV2ComponentInput(option.type, option);
        }
        this.subscriptions.push(
            this.tenantSvc.onInit().subscribe(() => {
                const tenant = this.tenantModelSvc.getTenant(option.type);
                if (tenant) {
                    this.selectedTenantProv = tenant.getAppCreateProvider();
                }
            })
        );
        const opts: ITCITenantCacheOptions = {
            routeId: TCIMainMenu.MAIN_MENU_APPLICATIONS.getRouteId(),
            subRouteId: TCIMainMenuSubroutes.TCI_APPLICATION_APP_LIST.getSubRouteId(),
            tenantType: option.type
        };
        this.cacheSvc.getCache(false, opts)
            .subscribe(() => {
                this.selectedOption = option;
                this.allowToPublish = false;
                this.input = {tenantType: option.type, create_option: option.subButtons[0] as ITCIApplicationCreateOption};
                this.tenantInput = new TCITenantCreateAppV2ComponentInput(
                    option.type,
                    option,
                    this.selectedMetaData,
                    this.isTwoOptionButtonEqual(option, this.selectedMetaData.recommendOption)
                );
                this.tenantMarketplaceInput = new TCITenantCreateAppMarketplaceV2ComponentInput(option.type, option, this.selectedMetaData.mkTags);
                this.selectedOptionBtns = option.subButtons;
                if (!this.isSelectedQuickstart && !this.isSelectedScratch) {
                    this.checkScrollButton();
                }
            });
    }

    // Note: when user selected scratch app option button, only show it in specified row.
    isShowScratchOptionButtonContent(indexNull: number, scratch: TCITenantAppTypeMetaData) {
        let selectedOptionButtonIndex = lodash.findIndex(scratch.buttons, item => this.selectedOption && item === this.selectedOption);
        // Note: when index selectedOptionButtonIndex is 0, set it to be 1, button will be on the first row
        selectedOptionButtonIndex = selectedOptionButtonIndex || 1;
        return scratch.equals(this.selectedScratchMetaData) &&
            selectedOptionButtonIndex > -1 &&
            // Note: array scratch.buttons looks like [a,b,c,e,f,null,1,2,3,4,5,null]
            //       left side of the equation: selected option button is in which row
            //       right side of the equation: the indexNull is in which row
            //       the null will convert to the element of tenant-dync-load (number of null == number of tenant-dync-load element)
            Math.ceil(selectedOptionButtonIndex / this.maxOptionButtonInRow) === Math.ceil(indexNull / (this.maxOptionButtonInRow + 1));
    }

    isTwoOptionButtonEqual(button1: TCIApplicationCreateOption, button2: TCIApplicationCreateOption) {
        return button1 && button2 && button1.equals(button2);
    }

    resetSelectOptionButton() {
        this.showImportCmp = false;
        this.selectedMKListing = null;
        this.allowToPublish = false;
        this.selectedTenantCanCreate = true;
        this.selectedOptionBtns = [];
        this.warns = [];
        delete this.input;
        delete this.tenantInput;
        delete this.tenantMarketplaceInput;
        delete this.tenantImportInput;
        delete this.tenantAPIChooserInput;
        delete this.tenantGraphQLSchemaInput;
        delete this.selectedBtn;
        delete this.selectedTenantProv;
    }
    resetCreateDialog() {
        this.resetSelectOptionButton();
        this.isSelectedQuickstart = false;
        this.isSelectedScratch = false;
        delete this.selectedScratchMetaData;
        delete this.selectedOption;
        delete this.selectedMetaData;
    }

    scrollOptionButton(isScrollRight: boolean = true) {
        const $ele = this.optionButtonScrollEle.nativeElement;
        const style = $ele.firstElementChild.currentStyle || window.getComputedStyle($ele.firstElementChild);
        const cardWidth = $ele.firstElementChild.offsetWidth + parseInt(style.marginLeft, 10) + parseInt(style.marginRight, 10);
        if (isScrollRight) {
            $ele.scrollLeft += cardWidth;
        } else {
            $ele.scrollLeft -= cardWidth;
        }
        this.checkScrollButton();
    }

    clearWarning() {
        this.warns = [];
    }

    getTenantTypeIcon(createOption: TCIApplicationCreateOption): string {
        let iconName = "ng-app-ic-" + createOption.type.toString().toLowerCase() + ".svg";
        if (createOption.equals(TCIApplicationCreateOption.IMPLEMENT_AN_OPENAPI_SPEC)) {
            iconName = "ng-app-ic-openapi.svg";
        } else if (createOption.equals(TCIApplicationCreateOption.IMPORT_A_GRAPHQL_SCHEMA)) {
            iconName = "ng-app-ic-graphql.svg";
        } else if (createOption.equals(TCIApplicationCreateOption.IMPORT_A_LIVE_APPS_APP)) {
            iconName = "ng-app-ic-liveapps-import.svg";
        } else if (createOption.equals(TCIApplicationCreateOption.IMPORT_A_FLOGO_APP)) {
            iconName = "ng-app-ic-flogo-import.svg";
        } else if (createOption.equals(TCIApplicationCreateOption.IMPORT_A_SCHEDULED_APP) ||
            createOption.equals(TCIApplicationCreateOption.IMPORT_A_EVENT_APP)) {
            iconName = "ng-app-ic-scribe-import.svg";
        }
        return iconName;
    }

    formatButtonLabel(label: string, isLineBreak: boolean = true, isZoom: boolean = true) {
        if (isLineBreak) {
            label = label.replace(" a ", " a<span>").replace(" an ", " an<span>") + "</span>";
            if (!isZoom) {
                label = label.replace("<span>", "<span class='new-line'>");
            }
        }
        if (isLineBreak && isZoom) {
            label = label.replace("<span>", "<span class='new-line zoom-text'>");
        }
        return label;
    }

    private resetScrollButton() {
        this.toolsSvc.setTimeout(() => {
            if (this.optionButtonScrollEle) {
                this.optionButtonScrollEle.nativeElement.scrollLeft = 0;
                this.checkScrollButton();
            }
        });
    }
    private checkScrollButton() {
        // Note: CSS scroll-behavior: smooth; caused dom scrollLeft number delay, need to calculate later
        // https://stackoverflow.com/questions/58140401/how-to-get-actual-scrollleft-of-a-element-when-using-scroll-behavior-smooth
        this.toolsSvc.setTimeout(() => {
            const $ele = this.optionButtonScrollEle.nativeElement;
            // Note: There is a 1px margin of error.
            const marginOfError = 1;
            this.isScrollLeftDisabled = Math.ceil($ele.scrollLeft) <= marginOfError;
            this.isScrollRightDisabled = (Math.ceil($ele.scrollLeft) + $ele.offsetWidth + marginOfError) >= $ele.scrollWidth;

            this.checkIsSelectedOptionButtonInView($ele);
        // scroll smoothly animation need to 1 second for firefox browser, attribute scrollLeft of element will be available after 1 second.
        }, 1000);
    }

    checkIsSelectedOptionButtonInView($ele) {
        const index = this.selectedMetaData.buttons.findIndex(item => this.selectedOption.equals(item));
        if (index > -1) {
            const container = $ele.getBoundingClientRect();
            const rect = $ele.querySelectorAll(".option-button")[index].getBoundingClientRect();
            const isInView = rect.x > container.x && rect.x < (container.x + container.width);
            this.isShowLeftScrollHintIcon = false;
            this.isShowRightScrollHintIcon = false;
            if (!isInView) {
                if (index < this.selectedMetaData.buttons.length / 2) {
                    this.isShowLeftScrollHintIcon = true;
                } else {
                    this.isShowRightScrollHintIcon = true;
                }
            }
        }
    }

    // after click left menu, the right side create app view should scroll to top
    private scrollViewToTop() {
        this.toolsSvc.setTimeout(() => {
            this.createAppDetailEle.nativeElement.scrollTop = 0;
        });
    }

    optBtnAction(option: TCIApplicationCreateOptionSubButton) {
        this.selectedBtn = option;
        this.input.create_option = option as ITCIApplicationCreateOption;

        const doNext = () => {
            if (TCIApplicationCreateOptionSubButton.isTenantUploadButton(option)
                || option.uiType === TCI_CREATE_APP_UI_TYPE.API_CHOOSER
                || option.uiType === TCI_CREATE_APP_UI_TYPE.DEFAULT_UI
                || option.uiType === TCI_CREATE_APP_UI_TYPE.EXTERNAL_URL) {
                this.createTenantApp(option);
                return;
            }

            // For "import / choose" screen
            this.selectedOptionBtns = [option];

            this.showImportCmp = option.uiType === TCI_CREATE_APP_UI_TYPE.FILE_UPLOAD;
            this.selectedTenantCanCreate = true;
        };

        const doError = (warns?: ITCIValidationResult) => {
            if (lodash.isUndefined(warns)) {
                warns = new TCIActionValidationResult();
                warns.type = TCIValidationResultType.VALIDATION_TYPE_ACTION;
                warns.state = TCIActionState.TCI_ACTION_ERROR;
                warns.setError(null, "This application couldn't be created");
            }

            this.warns = warns.getErrors();
            this.selectedTenantCanCreate = false;
        };

        this.selectedTenantProv.canCreate(this.input)
            .subscribe(
                (canCreateResult: ITCIValidationResult | boolean) => {
                    let canCreateResVal: ITCIValidationResult;
                    if (implementsTKeys<ITCIValidationResult>(
                        canCreateResult,
                        ["errors", "getType", "setError", "getErrors", "state"]
                    )) {
                        canCreateResVal = canCreateResult;
                        const errors = canCreateResVal.getErrors();
                        if (canCreateResVal.state === TCIActionState.TCI_ACTION_OK || errors.length === 0) {
                            doNext();
                            return;
                        }
                    } else if (lodash.isEqual(canCreateResult, true)) {
                        doNext();
                        return;
                    }
                    doError(canCreateResVal);
                },
                err => console.warn(err)
            );
    }

    evaluateTenantOutput($event: { DroppedFiles: ITCIUploadFileCheckResult[] } | boolean | number) {
        // Cleating input
        this.input.appData = {};
        this.input.appInputId = null;

        const defaultSubButton = this.selectedOptionBtns[0];
        if (defaultSubButton.uiType === TCI_CREATE_APP_UI_TYPE.FILE_UPLOAD || TCIApplicationCreateOptionSubButton.isTenantUploadButton(defaultSubButton)) {
            this.clearWarning();
            if (($event as any).DroppedFiles) {
                this.droppedFiles(($event as any).DroppedFiles);
            } else if ($event === false) {
                // Dropped files are not valid but evaluated by child component
                this.allowToPublish = false;
            }
        } else if (defaultSubButton.uiType === TCI_CREATE_APP_UI_TYPE.API_CHOOSER) {
            /**
             * API chooser in flogo has 2 options: select or upload an api spec.
             */
            if (!isNaN($event as any)) {
                if ($event === false) {
                    this.allowToPublish = false;
                } else {
                    this.setTenantOption($event as number);
                    this.allowToPublish = true;
                }
            } else if (($event as any).DroppedFiles) {
                this.droppedFiles(($event as any).DroppedFiles);
            } else {
                console.warn("Wrong option choosen", $event);
            }
        } else {
            console.warn("Unexpected option selected", $event, defaultSubButton);
        }
    }

    evaluateTenantMarketplaceOutput($event: ITCIMarketplaceListing) {
        // Note: user clicked the link "Find more in the marketplace", will close create dialog and redirect page to /marketplace
        if ($event === null) {
            this.RequestEnd.emit();
            this.router.navigate(["marketplace"]);
        }
    }

    isEvaluatePushButton(button: TCIApplicationCreateOptionSubButton): boolean {
        return lodash.some([
            TCIApplicationCreateOptionSubButton.FLOGO_APP_IMPORT,
            TCIApplicationCreateOptionSubButton.BW_APP_IMPORT,
            TCIApplicationCreateOptionSubButton.NODE_APP_IMPORT,
            // TCIApplicationCreateOptionSubButton.FLOGO_IMPORT_API_SPEC,
            TCIApplicationCreateOptionSubButton.FLOGO_IMPORT_GRAPHQL_SPEC,
            TCIApplicationCreateOptionSubButton.FLOGO_SELECT_OPEN_API_SPEC,
            // TCIApplicationCreateOptionSubButton.FLOGO_SELECT_GRAPHQL_API_SPEC,
            TCIApplicationCreateOptionSubButton.SCRIBE_IMPORT_EVENT_APP,
            TCIApplicationCreateOptionSubButton.SCRIBE_IMPORT_SCHEDULED_APP,
        ], button);
    }

    isHorizontalLayout(createOption: ITCIApplicationCreateOption): boolean {
        return lodash.some([
            TCIApplicationCreateOption.CREATE_A_FLOGO_APP,
            TCIApplicationCreateOption.CREATE_A_SCHEDULED_APP,
            TCIApplicationCreateOption.CREATE_A_REPLICATION_APP,
            TCIApplicationCreateOption.CREATE_A_EVENT_APP,
            TCIApplicationCreateOption.CREATE_A_LIVEAPPS_APP,
            // TCIApplicationCreateOption.IMPORT_A_LIVE_APPS_APP
        ], createOption);
    }

    droppedFiles(files: ITCIUploadFileCheckResult[]) {
        this.allowToPublish = false;
        this.errorMsgHide();

        // File attachment
        this.input.appData = {};
        files.forEach(file => this.input.appData[file.name] = file.fileContent);

        this.selectedTenantProv.validate(this.input).subscribe(iTenantValidationResult => {
            const tenantValidationResult = new TCITenantValidationResult(iTenantValidationResult);
            if (tenantValidationResult.isOK()) {
                this.allowToPublish = true;
            } else if (tenantValidationResult.isWarning()) {
                console.warn("App Create returned Warning", tenantValidationResult.getWarnings().map(w => w.getErrorMessage()));
            } else {
                this.setErrorInCtrl(lodash.get(lodash.sample(tenantValidationResult.getErrors()), "errorMsg", "Unexpected error"));
            }
        });
    }

    /**
     * @param appData
     * @returns App name from uploaded manifest json file
     * IPAS-14346: Default name from manifest.json file is not taken for BW apps in 2.0
     */
    getNameFromManifest(appData: ITCIFileMap = {}): string {
        let manifestJSON = {};
        if (appData[MANIFEST_JSON]) {
            try {
                manifestJSON = JSON.parse(appData[MANIFEST_JSON].content);
            } catch (e) {
                console.error("Unable to parse file");
            }
        }
        // If undefined, then query param "appName" will not be passed to backend getAppName api call.
        return manifestJSON["name"];
    }

    createTenantApp(option: TCIApplicationCreateOptionSubButton) {
        this.RequestStart.emit();
        this.warns = [];
        const responseData: IAppCreationResponse = {
            solution: {type: this.selectedOption.type},
            response: null
        };
        this.AppsAPISvc.getAppName(this.input.tenantType, this.getNameFromManifest(this.input.appData)).subscribe(
            nameInfo => { // Get app name
                this.input.name = nameInfo.name;
                this.input.create_option = option as ITCIApplicationCreateOption;
                const tenantProvider = this.tenantModelSvc.getTenant(this.selectedOption.type).getAppCreateProvider();

                tenantProvider.validate(this.input)
                    .subscribe(
                        val => {
                            if (val.state === TCIActionState.TCI_ACTION_OK) {
                                tenantProvider
                                    .createApplication(this.input)
                                    .subscribe(
                                        res => {
                                            responseData.response = res;
                                            if (option.uiType === TCI_CREATE_APP_UI_TYPE.EXTERNAL_URL) {
                                                this.RequestEnd.emit();
                                                if (res.success) {
                                                    this.ToastrSvc.success(`${res.name} application`, "has been created");
                                                    this.rememberUsedCreateOption(this.selectedOption);
                                                    this.RefreshAppList.emit();
                                                    console.log(`${res.name} application has been created, refresh app list to get new liveapps app.`);
                                                } else {
                                                    if (res.msg) {
                                                        this.ToastrSvc.error(res.msg);
                                                        console.log(`Create app failed, no need to refresh app list.`);
                                                    } else {
                                                        console.log(`The popup window is closed without saving, nothing need to do.`);
                                                    }
                                                }
                                            } else if (res.success && res.warnings && res.warnings.errors.length > 0) {
                                                this.warns = res.warnings.getErrors();
                                                this.response = responseData;
                                                this.rememberUsedCreateOption(this.selectedOption);
                                                this.RequestEndWithWarnings.emit();
                                            } else if (res.success) {
                                                // console.log("appCreateSucceed", res);
                                                this.ToastrSvc.success(`${res.name} application`, "has been created");
                                                this.RequestSucceed.emit(responseData);
                                                this.rememberUsedCreateOption(this.selectedOption);
                                                this.RequestEnd.emit();
                                            } else {
                                                this.setErrorHeader(responseData);
                                                // this.RequestEnd.emit(); // Don't send this on error or it will close the modal
                                            }
                                        },
                                        err => {
                                            responseData.response = err;
                                            this.setErrorHeader(responseData);
                                        });
                            } else {
                                // IPAS-16060 - DO NOT use the toastr to display the errors!!
                                // This method should return a "TCIActionValidationResult" response
                                // with warnings/errors set so this modal window can list them.

                                // const mainErr = val.getErrors().pop().toString();
                                // this.ToastrSvc.error("Invalid: " + mainErr); << WRONG
                                console.warn("Tenant Validation result:", val);

                                // Set the response value and pass the "val" as result attribute
                                responseData.response = {success: false, appId: null, name: null, result: val};
                                this.setErrorHeader(responseData);
                                // this.RequestEnd.emit(); // Don't send this on error or it will close the modal
                            }
                        },
                        err => {
                            console.error("Error in validation", err);
                            this.setErrorHeader();
                            // this.RequestEnd.emit(); // Don't send this on error or it will close the modal
                        }
                    );
            },
            err => {
                responseData.response = err;
                this.RequestFailed.emit(responseData);
            });
    }

    // IPAS-13426 - Show errors at the warnings list
    private setErrorHeader(responseData?: IAppCreationResponse) {
        // Take the erros or warnings from the response
        if (responseData && responseData.response && responseData.response.result) {
            this.warns = [
                ...responseData.response.result.getErrors(),
                ...responseData.response.result.getWarnings()
            ];
        }

        if (this.warns.length === 0) { // Send default error is none is present
            this.warns = [new ValidationError(null, lodash.get(responseData, "response.msg", "Unexpected error"), false)];
        }

        this.selectedTenantCanCreate = false; // To control the footer buttons
        this.response = responseData;
        this.RequestEndWithWarnings.emit(); // This only closes the toastrs
        this.RequestFailed.emit(responseData);
    }

    setErrorInCtrl(msg: string) {
        this.ImporterController.getMountedComponent().setErrorMsg(msg);
    }

    errorMsgHide() {
        this.ImporterController.getMountedComponent().errorMsgHide();
    }

    /**
     * For some tenants such as Mock the tenant component emits
     * parameters needed for it to process the application creation
     */
    setTenantOption(option: number) {
        this.input.appInputId = option;
    }

    cancel() {
        this.Close.emit();
    }

    finish() {
        this.RequestSucceed.emit(this.response);
    }

    toggleOptionGroup(catId) {
        this.toggleOptionGroupMap[catId] = !this.toggleOptionGroupMap[catId];
    }
}
